// src/components/CustomFieldDesigner.jsx
import React, { useState } from 'react';

const INPUT_TYPES = [
  'text',
  'textarea',
  'number',
  'email',
  'date',
  'select',
  'file',
];

// full label -> snake_key (keeps full string, underscores for spaces)
function slugify(s = '') {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_') // spaces & non-alnum -> _
    .replace(/^_+|_+$/g, ''); // no leading/trailing _
}

export default function CustomFieldDesigner({ customFields, setCustomFields }) {
  const [label, setLabel] = useState('');
  const [key, setKey] = useState('');
  const [inputType, setInputType] = useState('text');
  const [placeholder, setPlaceholder] = useState('');
  const [required, setRequired] = useState(true);

  // track if user manually edited key (then stop auto-sync from label)
  const [manualKeyEdit, setManualKeyEdit] = useState(false);

  // options state for select
  const [options, setOptions] = useState([]);
  const [optionLabel, setOptionLabel] = useState('');
  const [optionValue, setOptionValue] = useState('');

  // validation errors for the field
  const [fieldError, setFieldError] = useState('');
  const [optionError, setOptionError] = useState('');

  const onAddOption = () => {
    setOptionError('');

    if (!optionLabel.trim()) {
      setOptionError('Option label is required');
      return;
    }

    const lbl = optionLabel.trim();
    const val = optionValue.trim() || slugify(lbl) || lbl;

    // avoid duplicate value
    if (options.some((o) => o.value === val)) {
      setOptionError('This option value already exists');
      return;
    }

    setOptions((prev) => [...prev, { label: lbl, value: val }]);
    setOptionLabel('');
    setOptionValue('');
  };

  const removeOption = (value) => {
    setOptions((prev) => prev.filter((o) => o.value !== value));
  };

  const onAdd = () => {
    setFieldError('');

    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setFieldError('Label is required');
      return;
    }

    const finalKey = (key.trim() || slugify(trimmedLabel)) || '';
    if (!finalKey) {
      setFieldError('Key is required');
      return;
    }

    if (!inputType) {
      setFieldError('Type is required');
      return;
    }

    if (inputType === 'select' && options.length < 2) {
      setFieldError('Please add at least 2 options for a select field');
      return;
    }

    // prevent duplicate keys
    if (customFields.some((f) => f.key === finalKey)) {
      setFieldError('A field with this key already exists');
      return;
    }

    const newField = {
      // 🔹 match your backend payload
      label: trimmedLabel,
      key: finalKey,
      type: inputType,
      options: inputType === 'select' ? options : [],
      status: 'pending',
      placeholder: placeholder.trim(),
      value: '',
      required: !!required,
    };

    setCustomFields((prev) => [...prev, newField]);

    // reset
    setLabel('');
    setKey('');
    setPlaceholder('');
    setInputType('text');
    setRequired(true);
    setManualKeyEdit(false);
    setOptions([]);
    setOptionLabel('');
    setOptionValue('');
    setFieldError('');
    setOptionError('');
  };

  const removeField = (k) => {
    setCustomFields((prev) => prev.filter((f) => f.key !== k));
  };

  return (
    <div className="cfd">
      <div className="cfd-row">
        <div className="cfd-col">
          <label>Label</label>
          <input
            className="cfd-input"
            value={label}
            onChange={(e) => {
              const v = e.target.value;
              setLabel(v);
              // Auto-sync key with FULL label (spaces -> _), unless user edited key manually
              if (!manualKeyEdit) setKey(slugify(v));
            }}
            placeholder="e.g. Profile Photo"
          />
        </div>

        <div className="cfd-col">
          <label>Key</label>
          <input
            className="cfd-input"
            value={key}
            onChange={(e) => {
              setManualKeyEdit(true); // user overrides -> stop auto-sync from label

              const raw = e.target.value;
              // Only replace spaces with "_" in key input
              const transformed = raw.replace(/\s+/g, '_');
              setKey(transformed);
            }}
            placeholder="auto from label"
          />
        </div>

        <div className="cfd-col">
          <label>Type</label>
          <select
            className="cfd-input"
            value={inputType}
            onChange={(e) => {
              setInputType(e.target.value);
              setFieldError('');
            }}
          >
            {INPUT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="cfd-row">
        <div className="cfd-col">
          <label>Placeholder</label>
          <input
            className="cfd-input"
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            placeholder="Shown as hint (optional)"
          />
        </div>

        {/* Required flag */}
        <div className="cfd-col">
          <label>Required</label>
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <span>Yes</span>
          </div>
        </div>

        {/* Select options UI */}
        {inputType === 'select' && (
          <>
            <div className="cfd-col">
              <label>Option Label</label>
              <input
                className="cfd-input"
                value={optionLabel}
                onChange={(e) => setOptionLabel(e.target.value)}
                placeholder="e.g. High"
              />
            </div>

            <div className="cfd-col">
              <label>Option Value</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="cfd-input"
                  style={{ flex: 1 }}
                  value={optionValue}
                  onChange={(e) =>
                    setOptionValue(e.target.value.replace(/\s+/g, '_'))
                  }
                  placeholder="e.g. high (auto if empty)"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onAddOption}
                >
                  + Add Option
                </button>
              </div>
            </div>
          </>
        )}

        {inputType !== 'select' && (
          <div className="cfd-col cfd-add-btn">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={onAdd}
            >
              + Add Field
            </button>
          </div>
        )}
      </div>

      {/* Show option validation error */}
      {optionError && (
        <div className="cfd-error">
          <small style={{ color: 'red' }}>{optionError}</small>
        </div>
      )}

      {/* Show options list for select */}
      {inputType === 'select' && options.length > 0 && (
        <div className="cfd-list mb-2">
          {options.map((opt) => (
            <div className="cfd-chip" key={opt.value}>
              <span>
                {opt.label} <em>({opt.value})</em>
              </span>
              <button
                type="button"
                className="cfd-chip-x"
                onClick={() => removeOption(opt.value)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Field button (for select, move below options) */}
      {inputType === 'select' && (
        <div className="cfd-add-btn" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={onAdd}
          >
            + Add Field
          </button>
        </div>
      )}

      {/* Show field validation error */}
      {fieldError && (
        <div className="cfd-error" style={{ marginTop: 8 }}>
          <small style={{ color: 'red' }}>{fieldError}</small>
        </div>
      )}

      {/* Show added fields */}
      {customFields.length > 0 && (
        <div className="cfd-list" style={{ marginTop: 12 }}>
          {customFields.map((f) => (
            <div className="cfd-chip" key={f.key}>
              <span>
                {f.label}{' '}
                <em>
                  ({f.type}){f.required ? ' *' : ''}
                </em>
              </span>
              <button
                type="button"
                className="cfd-chip-x"
                onClick={() => removeField(f.key)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
